import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { getCampaignApi, listCampaignsApi, type CampaignListItem, type SuggestedAd } from "../campanhasApi";
import { addCreativeAsAdApi } from "../criativosApi";

const OPEN_STATUSES = ["RASCUNHO", "PAUSADA", "ATIVA", "ERRO"];

type Props = {
  creativeId: string;
  defaultCampaignId: string | null;
  onClose: () => void;
  onDone: (campaignId: string) => void;
};

/** Vira anúncio de uma campanha: a imagem desta versão e os três textos. */
const UseCreativeAsAdModal: React.FC<Props> = ({ creativeId, defaultCampaignId, onClose, onDone }) => {
  const [campaigns, setCampaigns] = useState<CampaignListItem[] | null>(null);
  const [campaignId, setCampaignId] = useState(defaultCampaignId ?? "");
  const [suggested, setSuggested] = useState<SuggestedAd[]>([]);
  const [primaryText, setPrimaryText] = useState("");
  const [headline, setHeadline] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listCampaignsApi()
      .then((list) => setCampaigns(list.filter((c) => OPEN_STATUSES.includes(c.status))))
      .catch((e) => {
        toast.error(e?.message);
        setCampaigns([]);
      });
  }, []);

  useEffect(() => {
    setSuggested([]);
    if (!campaignId) return;
    getCampaignApi(campaignId)
      .then((c) => setSuggested(c.suggestedAds ?? []))
      .catch(() => undefined);
  }, [campaignId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaignId) return toast.error("Escolha a campanha.");
    try {
      setBusy(true);
      const ad = await addCreativeAsAdApi(creativeId, {
        campaignId,
        primaryText: primaryText.trim(),
        headline: headline.trim(),
        description: description.trim() || undefined,
      });
      toast.success(`Incluído como anúncio ${ad.position} da campanha.`);
      onDone(campaignId);
    } catch (err: any) {
      toast.error(err?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ecoDialog__overlay">
      <form className="cmpModal" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="crv-ad-title">
        <div className="cmpModal__head">
          <h2 id="crv-ad-title" className="cmpModal__title">
            Usar como anúncio
          </h2>
          <button type="button" className="ecoBtn ecoBtn--icon" aria-label="Fechar" data-modal-close onClick={onClose} disabled={busy}>
            <EcoIcon name="x" size={18} />
          </button>
        </div>

        <label className="ecoField">
          <span className="ecoLabel">Campanha</span>
          <select className="ecoInput" required value={campaignId} onChange={(e) => setCampaignId(e.target.value)} disabled={!campaigns || busy}>
            <option value="">{campaigns ? "Escolha a campanha" : "Carregando..."}</option>
            {(campaigns ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <span className="cmpHint">Campanha publicada: o anúncio novo vai para a revisão da Meta.</span>
        </label>

        {suggested.length ? (
          <div className="aiSuggestedTexts">
            <span className="ecoLabel">Textos sugeridos pela Eco-IA para esta campanha</span>
            <div className="aiDrawer__starters">
              {suggested.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="aiChip"
                  title={s.primaryText}
                  onClick={() => {
                    setPrimaryText(s.primaryText);
                    setHeadline(s.headline.slice(0, 40));
                    setDescription((s.description ?? "").slice(0, 30));
                  }}
                >
                  {s.angle ? `${s.angle}: ` : ""}
                  {s.headline}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <label className="ecoField">
          <span className="ecoLabel">Texto principal</span>
          <textarea className="ecoInput cmpTextarea" rows={4} maxLength={500} required value={primaryText} onChange={(e) => setPrimaryText(e.target.value)} />
          <span className="cmpCount">{primaryText.length}/500</span>
        </label>
        <div className="cmpGrid">
          <label className="ecoField">
            <span className="ecoLabel">Título</span>
            <input className="ecoInput" maxLength={40} required value={headline} onChange={(e) => setHeadline(e.target.value)} />
            <span className="cmpCount">{headline.length}/40</span>
          </label>
          <label className="ecoField">
            <span className="ecoLabel">
              Descrição <span className="ecoLabel__optional">(opcional)</span>
            </span>
            <input className="ecoInput" maxLength={30} value={description} onChange={(e) => setDescription(e.target.value)} />
            <span className="cmpCount">{description.length}/30</span>
          </label>
        </div>

        <div className="cmpFooter">
          <button type="button" className="ecoBtn" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy}>
            {busy ? "Incluindo..." : "Incluir anúncio"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UseCreativeAsAdModal;
